/**
 * apiRequests.ts
 * -----------------
 * This file contains general functions used to perform
 * the requests to the server.
 */
import Cookies from "js-cookie";
import mongoose from "mongoose";
import { BACKEND_URL } from "config/getUrl";
import { validateClient } from "./authRequests";
import { Message } from "utils/interfaces";

export const getCollectionRequest = async (
  urlAddress: string,
  fields: string[] = [],
  filter: Object = {},
  populateOptions: mongoose.PopulateOptions[] = [],
  customQuery: { [key: string]: string } = {}
) => {
  // Validate the tokens
  const validateResponse = await validateClient();

  // Check if the user is still connected
  if (!validateResponse.connected) {
    return {
      message: "It was not possible to find an access or refresh token.",
      successful: false,
      logout: true,
    };
  }

  // Get the accessToken
  const accessToken = Cookies.get("accessToken");

  // Check that the access token is not undefined
  if (!accessToken) {
    return {
      message: "It was not possible to find an access token.",
      successful: false,
      logout: true,
    };
  }

  // Sending to the server
  const options: RequestInit = {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  // Define the field query
  const queryObj: {
    fields?: string;
    filter?: string;
    populateOptions?: string;
  } = { ...customQuery };

  // Transform fields into a string
  const fieldsString = fields.join(" ");
  // Transform filter into a string
  const filterString = JSON.stringify(filter);
  // Transform populate option into string
  const populateString = JSON.stringify(populateOptions);

  if (fieldsString) queryObj.fields = fieldsString;
  if (filterString !== "{}") queryObj.filter = filterString;
  if (populateString !== "[]") queryObj.populateOptions = populateString;

  // Define the field query
  const query = new URLSearchParams(queryObj);

  // Perform the requests
  const response = await fetch(
    `${BACKEND_URL}${urlAddress}${query.toString() ? "?" : ""}${query}`,
    options
  );

  // Check if the status of the answer is ok
  if (!(response.status === 200))
    return {
      message: `Request status: ${response.status}`,
      successful: false,
      logout: false,
    };

  // Get the response
  const data = await response.json();

  // Return the data
  return data;
};

export const createRecordRequest = async (
  record: object,
  url: string,
  contentType: string = "application/json"
) => {
  // Validate the tokens
  const validateResponse = await validateClient();

  // Check if the user is still connected
  if (!validateResponse.connected) {
    return {
      message: "It was not possible to find an access or refresh token.",
      successful: false,
      logout: true,
    };
  }

  // Get the accessToken
  const accessToken = Cookies.get("accessToken");

  // Check that the access token is not undefined
  if (!accessToken) {
    return {
      message: "It was not possible to find an access token.",
      successful: false,
      logout: true,
    };
  }

  // Set up the options for the request
  const options = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": contentType,
    },
    body: JSON.stringify(record),
  };

  // Post the record to the specified
  const response = await fetch(`${BACKEND_URL}${url}`, options);

  // Check if the status of the answer is ok
  if (response.status === 200) {
    // Get the response
    return await response.json();
  }
};

export const updateRecordRequest = async (
  infoToUpdate: object,
  url: string
) => {
  // Validate the tokens
  const validateResponse = await validateClient();

  // Check if the user is still connected
  if (!validateResponse.connected) {
    return {
      message: "It was not possible to find an access or refresh token.",
      successful: false,
      logout: true,
    };
  }

  // Get the accessToken
  const accessToken = Cookies.get("accessToken");

  // Check that the access token is not undefined
  if (!accessToken) {
    return {
      message: "It was not possible to find an access token.",
      successful: false,
      logout: true,
    };
  }

  // Sending to the server
  const options = {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(infoToUpdate),
  };

  // Post the user
  const response = await fetch(`${BACKEND_URL}${url}`, options);

  // Check if the status of the answer is ok
  if (response.status === 200) {
    // Get the response
    return await response.json();
  }
};

export const deleteRecordRequest = async (
  record: object,
  url: string,
  contentType: string = "application/json"
) => {
  // Validate the tokens
  const validateResponse = await validateClient();

  // Check if the user is still connected
  if (!validateResponse.connected) {
    return {
      message: "It was not possible to find an access or refresh token.",
      successful: false,
      logout: true,
    };
  }

  // Get the accessToken
  const accessToken = Cookies.get("accessToken");

  // Check that the access token is not undefined
  if (!accessToken) {
    return {
      message: "It was not possible to find an access token.",
      successful: false,
      logout: true,
    };
  }

  // Set up the options for the request
  const options = {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": contentType,
    },
    body: JSON.stringify(record),
  };

  // Post the record to the specified
  const response = await fetch(`${BACKEND_URL}${url}`, options);

  // Check if the status of the answer is ok
  if (response.status === 200) {
    // Get the response
    return await response.json();
  }
};

/**
 * Function to populate code editor with AWS files
 * @param type
 * @param lessonId
 * @returns
 */
export const populateEditor = async (
  type: string | undefined,
  lessonId: string
) => {
  // Validate the tokens
  const validateResponse = await validateClient();

  // Check if the user is still connected
  if (!validateResponse.connected) {
    return {
      message: "It was not possible to find an access or refresh token.",
      successful: false,
      logout: true,
    };
  }

  // Get the accessToken
  const accessToken = Cookies.get("accessToken");

  // Check that the access token is not undefined
  if (!accessToken) {
    return {
      message: "It was not possible to find an access token.",
      successful: false,
      logout: true,
    };
  }

  // Sending to the server
  const options: RequestInit = {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  // Check the parameters before sending them as a request
  if (type === undefined || (type !== "modules" && type !== "projects")) {
    throw new Error("type must be 'modules' or 'projects'");
  }

  try {
    const response = await fetch(
      `${BACKEND_URL}/api/lessons/editor-info?type=${type}&id=${lessonId}`,
      options
    );

    if (response.status === 200) {
      // Get the data
      const data = await response.json();

      // Check if the response was successful
      if (!data.successful) {
        console.error("Failed to fetch content: unsuccessful request");
      }

      // Get the content
      const content = data.content;

      return {
        title: content.title,
        videoUrl: content.videoUrl,
        planName: content.planName,
        language: content.language,
        videoTranscript: content.videoTranscript,
      };
    } else {
      throw new Error(`Failed to fetch content: ${response.statusText}`);
    }
  } catch (error) {
    console.error("Fetch error:", error);
  }
};

export const getChatMessages = async (
  type: string,
  id: string
): Promise<[error: string] | [error: undefined, messages: Message[]]> => {
  // Validate the tokens
  const validateResponse = await validateClient();

  // Check if the user is still connected
  if (!validateResponse.connected) {
    return ["It was not possible to find an access or refresh token."];
  }

  // Get the accessToken
  const accessToken = Cookies.get("accessToken");

  // Check that the access token is not undefined
  if (!accessToken) {
    return ["It was not possible to find an access token."];
  }

  const editorSession = process.env.REACT_APP_DEMO
    ? Cookies.get("editorSession") || ""
    : "";

  // Sending to the server
  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      type,
      id,
      session: editorSession,
    }),
  };

  const response = await fetch(
    `${BACKEND_URL}/api/assistant/messages`,
    options
  );
  if (!response.ok) {
    const error = await response.json();
    console.error(error);
    return [error];
  }
  return [undefined, await response.json()];
};

/**
 * Function used to check the existance of a list of entities
 * on different databases
 * @param record
 * @param url
 * @returns
 */
export const checkRecordsExistence = async (record: object, url: string) => {
  // Validate the tokens
  const validateResponse = await validateClient();

  // Check if the user is still connected
  if (!validateResponse.connected) {
    return {
      message: "It was not possible to find an access or refresh token.",
      successful: false,
      logout: true,
    };
  }

  // Get the accessToken
  const accessToken = Cookies.get("accessToken");

  // Check that the access token is not undefined
  if (!accessToken) {
    return {
      message: "It was not possible to find an access token.",
      successful: false,
      logout: true,
    };
  }

  // Set up the options for the request
  const options = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(record),
  };

  // Post the record to the specified
  const response = await fetch(`${BACKEND_URL}${url}`, options);

  // Check if the status of the answer is ok
  if (response.status === 200) {
    // Get the response
    return await response.json();
  }
};

/**
 * [Prototype] Function used to request a GET endpoint which needs to be authorised
 * @param url
 * @returns
 */
export const getAuthorisedRequest = async (
  url: string
): Promise<{
  message: string;
  successful: boolean;
  logout: boolean;
  response?: Response;
}> => {
  // Validate the tokens
  const validateResponse = await validateClient();

  // Check if the user is still connected
  if (!validateResponse.connected) {
    return {
      message: "It was not possible to find an access or refresh token.",
      successful: false,
      logout: true,
    };
  }

  // Get the accessToken
  const accessToken = Cookies.get("accessToken");

  // Check that the access token is not undefined
  if (!accessToken) {
    return {
      message: "It was not possible to find an access token.",
      successful: false,
      logout: true,
    };
  }

  // Sending to the server
  const options: RequestInit = {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  // Perform the requests
  const response = await fetch(`${BACKEND_URL}${url}`, options);

  return {
    message: "Request obtained successfully.",
    successful: true,
    logout: false,
    response,
  };
};
